<template>
    <div class="new-user-page">
        <router-link v-if="showBackButton" class="new-user-page__back" to="/">
            <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M23.376 15.125H12.9397L17.4772 10.6013C17.7361 10.3423 17.8816 9.99118 17.8816 9.62501C17.8816 9.25885 17.7361 8.90768 17.4772 8.64876C17.2183 8.38984 16.8671 8.24438 16.501 8.24438C16.1348 8.24438 15.7836 8.38984 15.5247 8.64876L8.64971 15.5238C8.52453 15.6545 8.4264 15.8087 8.36096 15.9775C8.22343 16.3123 8.22343 16.6878 8.36096 17.0225C8.4264 17.1913 8.52453 17.3455 8.64971 17.4763L15.5247 24.3513C15.6525 24.4801 15.8046 24.5824 15.9722 24.6522C16.1397 24.722 16.3194 24.758 16.501 24.758C16.6825 24.758 16.8622 24.722 17.0298 24.6522C17.1973 24.5824 17.3494 24.4801 17.4772 24.3513C17.6061 24.2234 17.7084 24.0714 17.7782 23.9038C17.848 23.7362 17.8839 23.5565 17.8839 23.375C17.8839 23.1935 17.848 23.0138 17.7782 22.8462C17.7084 22.6787 17.6061 22.5266 17.4772 22.3988L12.9397 17.875H23.376C23.7406 17.875 24.0904 17.7301 24.3482 17.4723C24.6061 17.2144 24.751 16.8647 24.751 16.5C24.751 16.1353 24.6061 15.7856 24.3482 15.5277C24.0904 15.2699 23.7406 15.125 23.376 15.125Z" fill="#202225" />
            </svg>
        </router-link>
        <div class="new-user-page__content">
            <logos-container center />
            <h1 class="new-user-page__title">
                {{ $t('Confirm your email') }}
            </h1>
            <p>
                {{ $t('You need to verify your email to complete registration') }}
            </p>
            <img class="mt-4 new-user-page__img" src="@/assets/images/icons/mail.png" alt="email-send-logo" />
            <p class="new-user-page__text" v-sanitize="$t('An email has been sent to <b>{email}</b> with a link to verify your account. If you haven\'t received the email after 2 minutes, please check your spam folder', { email: $route.query.email })" ></p>
            <form class="new-user-page__form" @submit.prevent="handleSubmit()">
                <b-alert v-if="tooManyRequests" show variant="danger" class="text-center mb-0">
                    {{ $t('You have exceeded your request limit, to continue with your registration you can start a support chat') }}.
                </b-alert>
                <ui-input
                    id="verification_code"
                    name="verification_code"
                    type="number"
                    class="text-left"
                    placeholder="000000"
                    v-model="verificationCode"
                    :label="$t('Code')"
                    :field="v$.verificationCode"
                />
                <ui-button
                    :loading="validatingCode"
                    class="w-100 mb-0"
                    type="submit"
                >
                    {{ $t('Validate code') }}
                </ui-button>
                <ui-button
                    v-if="showResendCodeButton"
                    :disabled="timeRemaining > 0 || validatingCode || tooManyRequests || sendingCode"
                    :loading="sendingCode"
                    class="w-100 mb-0"
                    variant="secondary"
                    @click="sendCodeRecaptcha()"
                >
                    {{ $t('Resend code') }}
                </ui-button>
            </form>
            <p v-if="showResendCodeButton && timeRemaining > 0" class="phone-verification-form__wait-message">
                {{ $t('It is necessary to wait to resend the code {seconds} seconds', { seconds: timeRemaining }) }}
            </p>
            <p class="new-user-page__support-message">
                <strong>{{ $t('If you are having trouble receiving the verification code, please start a support chat.') }}</strong>
            </p>
        </div>
        <vue-recaptcha
            class="w-100"
            ref="recaptcha"
            size="invisible"
            @verify="validateCode"
            :sitekey="CONST.CAPTCHACODE"
            :loadRecaptchaScript="true"
        />
        <vue-recaptcha
            class="w-100"
            ref="sendCodeRecaptcha"
            size="invisible"
            loadRecaptchaScript
            :sitekey="CONST.CAPTCHACODE"
            @verify="sendCode"
        />
    </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { helpers, maxLength, minLength, required } from '@vuelidate/validators';
import { io } from 'socket.io-client';
import VueRecaptcha from 'vue-recaptcha';

import LogosContainer from '@/components/ui/LogosContainer.vue';
import UiButton from '@/components/ui/buttons/Button.vue';
import UiInput from '@/components/ui/inputs/Input.vue';

import recaptchaUtils from '@/utils/recaptcha.utils';

export default {
    name: 'new-user-page',
    components: {
        LogosContainer,
        UiButton,
        UiInput,
        VueRecaptcha,
    },
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            loadingLogo: false,
            showBackButton: true,
            validatingCode: false,
            verificationCode: '',
            sendingCode: false,
            timeInterval: null,
            timeRemaining: 0,
            tooManyRequests: false,
            waitTime: 30,
        };
    },
    validations() {
        return {
            verificationCode: {
                required: helpers.withMessage(this.$t('validations.required', { field: this.$t('code') }), required),
                minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params ), minLength(6)),
                maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params ), maxLength(6)),
                $autoDirty: true,
            },
        };
    },
    computed: {
        showResendCodeButton() {
            return false;
        },
    },
    mounted() {
        const socket = io(window.ENV.HOSTNAME, {
            transports: ['websocket'],
            auth: { token: this.$cookies.get('_skt') }
        });

        socket.on('email.verified', data => {
            const formatted_url = new URL(data.redirect_url);
            const webview = this.$route.query.webview;

            if (webview) {
                formatted_url.searchParams.set('webview', webview);
            }

            window.location.replace(formatted_url.toString());
        });

        const { redirect_url } = this.$route.query

        if (redirect_url) {
            this.showBackButton = false;
        }

        if (this.showResendCodeButton) {
            this.$nextTick(() => {
                this.setTimeRemaining();
            });
        }
    },
    methods: {
        sendCodeRecaptcha() {
            if (!recaptchaUtils.isAvailable()) {
                this.sendCode('');
                return;
            }

            this.$refs.sendCodeRecaptcha.reset();
            this.$refs.sendCodeRecaptcha.execute();
        },
        setTimeRemaining() {
            this.timeRemaining = this.waitTime;
            this.timeInterval = setInterval(() => {
                if (this.timeRemaining > 0) {
                    this.timeRemaining--;
                } else {
                    clearInterval(this.timeInterval);
                }
            }, 1000);
        },
        async handleSubmit() {
            this.v$.$touch();

            if (this.v$.$invalid) {
                return;
            }

            if (!recaptchaUtils.isAvailable()) {
                this.validateCode('');
                return;
            }

            this.$refs.recaptcha.reset();
            this.$refs.recaptcha.execute();
        },
        async sendCode(token) {
            try {
                this.sendingCode = true;

                const { email } = this.$route.query;
                const config = { auth: { username: email, password: '' } };
                const payload = {
                    type: 'email',
                    action: 'email_verification',
                    message: '',
                    from: 'Tendencys',
                    token,
                };

                await this.axiosAccount.post('/api/notifications', payload, config);
                this.setTimeRemaining();
            } catch(error) {
                if (error.response.status === 429) {
                    this.tooManyRequests = true;
                    return;
                }

                this.showError(error);
            } finally {
                this.sendingCode = false;
            }
        },
        async validateCode(token) {            
            try {
                this.validatingCode = true;
                
                const { email, site_id, redirect_url, webview } = this.$route.query;
                                
                const config = { auth: { username: email, password: '' } };
                const payload = { code: this.verificationCode, token };

                const response = await this.axiosAccount.post('/api/notifications/code-verification/email', payload, config);
                if (response.status === 202) {
                    this.$toasted.error(this.$t('We could not verify your email address. Please try again.'));
                    return;
                }
                const allParams = { email, site_id, redirect_url, webview };
                const queryParams = {};
                
                Object.entries(allParams).forEach(([key, value]) => {
                    if (value && value !== 'undefined' && value !== 'null') {
                        queryParams[key] = value;
                    }
                });
                const loginUrl = `/login?${new URLSearchParams(queryParams).toString()}`;                
                window.location.href = loginUrl;
            } catch (error) {
                this.showError(error);
            } finally {
                this.validatingCode = false
            }
        },
    },
};
</script>

<style lang="scss">
.new-user-page {
    text-align: center;
    position: relative;

    &__back {
        margin-right: 100%;

        @media(min-width: 992px) {
            position: absolute;
            left: 0;
            top: 0;
        }
    }

    &__content {
        max-width: 650px;
        margin-left: auto;
        margin-right: auto;
    }

    &__logo {
        margin-bottom: 35px;
    }

    &__title {
        font-weight: 700;
    }

    &__text {
        margin-left: auto;
        margin-right: auto;
        max-width: 100%;
        width: 500px;
    }

    &__img {
        height: auto;
        margin-bottom: 30px;
        max-width: 118px;
        width: 100%;
    }

    &__form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 24px;
        margin-left: auto;
        margin-right: auto;
        max-width: 100%;
        width: 450px;
    }

    &__support-message {
        font-size: 0.875rem;
        margin-bottom: 0;
        margin-left: auto;
        margin-right: auto;
        width: 450px;
    }
}
</style>
